import {
  ONBOARD_CLIENT,
  CREATE_CASE,
  UPDATE_CASE,
  FETCH_CASE,
  FETCH_CLIENT,
  DELETE_CASE_FIELD,
  ANALYZE_CASE,
  FETCH_ALL_CLIENTS,
  GENERATE_DOCUMENT_FAILURE,
  GENERATE_DOCUMENT_REQUEST,
  GENERATE_DOCUMENT_SUCCESS,
  FETCH_ALL_EXTERNAL_CASES,
  FETCH_ALL_CLIENT_DETAILS_SUCCESS,
  FETCH_ALL_CLIENT_DETAILS_FAILURE,
  ONBOARD_CLIENT_RESET,
  ONBOARD_CLIENT_CONNECTING,
  ONBOARD_CLIENT_CONNECTED,
  ONBOARD_CLIENT_STEPS,
  ONBOARD_CLIENT_STEP,
  ONBOARD_CLIENT_COMPLETED,
  ONBOARD_CLIENT_FAILED,
} from "../types";

const FROM_RAW: Record<string, "Pending" | "In Progress" | "Completed" | "Failed" | "Skipped"> = {
  pending: "Pending",
  in_progress: "In Progress",
  completed: "Completed",
  failed: "Failed",
  skipped: "Skipped",
};

type StepUI = {
  id: string;
  label: string;
  status: "Pending" | "In Progress" | "Completed" | "Failed" | "Skipped";
  messages: string[];
};

export interface ApiClientData {
  // Old API format fields
  ClientID?: string;
  IDVerificationStatus?: string;
  Email?: string;
  Name?: string;
  ClientType?: string;
  UserTypeName?: string;
  PlatformCaseCount?: number;
  ExternalCaseCount?: number;
  TotalValue?: number;
  FirstName?: string;
  LastName?: string;
  BusinessName?: string;

  // New API format fields
  id?: number | string;
  name?: string;
  type_id?: number;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  notes?: string | null;
  is_active?: boolean;
  organization_id?: number;
  created_at?: string;
  updated_at?: string;
  uuid?: string;
  deleted_at?: string | null;
  client_metadata?: Record<string, any>;
  last_modified_by_user_id?: number;
  last_modified_by_user_uuid?: string;
}

export interface IndividualClientData {
  client_type?: 'individual';
  UserTypeName: 'IndividualClient';
  FirstName: string;
  LastName: string;
  Email: string;
  PhoneNumber: string;
  IDVerificationStatus?: string;
  Address: string;
  UserID?: string;
  ClientID: string;
  attachments?: File[];
  BusinessName?: string;
}

export interface BusinessClientData {
  client_type?: 'business';
  UserTypeName: 'BusinessAdmin';
  BusinessName: string;
  FirstName: string;
  LastName: string;
  IDVerificationStatus?: string;
  Email: string;
  PhoneNumber: string;
  Address: string;
  BusinessID?: string;
  UserID?: string;
  ClientID: string;
  attachments?: File[];
}


export type ClientData = IndividualClientData | BusinessClientData;

export interface CaseData {
  client_id?: string;
  case_id?: string;
  business_id?: string;
  case_type?: string;
  case_status?: string;
  case_conclusion?: string;
  lawyer_id?: string;
  main_issue?: string;
  case_summary?: string;
  plaintiff_info?: string;
  defendant_info?: string;
  witness_statements?: string;
  evidence?: string;
  legal_arguments?: string;
  court_dates?: string;
  custom_fields?: Record<string, string>;
}

interface ClientState {
  importProgress: {
    active: boolean;
    messages: any[];
    jobId: any;
    percentage: number;
  };
  clientData: any;
  cases: Record<string, any>;
  selectedCase: any;
  clientDetails: { [key: string]: any };
  selectedClient: any;
  allClients: ApiClientData[];
  generatingDocument: boolean;
  documentGenerationError: string | null;
  allExternalCases: any[];
  selectedClients: any[];
  isConnecting: boolean;
  connected: boolean;
  steps: StepUI[];
  lastStep?: StepUI | null;
  complete: boolean;
  ok: boolean | null;
  summary: any,
  error: any
}

const initialState: ClientState = {
  importProgress: {
    active: false,
    messages: [],
    jobId: null,
    percentage: 0,
  },
  clientData: {},
  cases: {},
  selectedCase: {},
  clientDetails: {},
  selectedClient: {},
  allClients: [],
  generatingDocument: false,
  documentGenerationError: null,
  allExternalCases: [],
  selectedClients: [],
  isConnecting: false,
  connected: false,
  steps: [],
  complete: false,
  ok: null,
  summary: null,
  error: null
};

function upsertStep(steps: StepUI[], incoming: Partial<StepUI> & { id: string }): StepUI[] {
  const idx = steps.findIndex((s) => s.id === incoming.id);

  if (idx === -1) {
    return [
      ...steps,
      {
        ...incoming, 
        id: incoming.id,
        label: incoming.label ?? incoming.id,
        status: (incoming.status as StepUI["status"]) || "Pending",
        messages: incoming.messages ?? [],
      },
    ];
  }

  const prev = steps[idx];
  const next: StepUI = {
    ...prev,
    ...incoming,
    id: incoming.id,
    label: incoming.label ?? prev.label ?? incoming.id,
    status: (incoming.status as StepUI["status"]) || prev.status,
    messages: incoming.messages ?? prev.messages ?? [],
  };

  const copy = [...steps];
  copy[idx] = next;
  return copy;
}


export const clientReducer = (state = initialState, action: any): ClientState => {
  switch (action.type) {
    case FETCH_ALL_CLIENTS:
      // Normalize client data to handle both old (ClientID, Name) and new (id, name) API formats
      const normalizedClients = Array.isArray(action.payload)
        ? action.payload.map((client: any) => ({
            // Spread all original fields first
            ...client,
            // Then apply normalized fields (overrides id/name with ClientID/Name)
            ClientID: client.ClientID || String(client.id),
            Name: client.Name || client.name,
          }))
        : [];
      return {
        ...state,
        allClients: normalizedClients
      };
    case ONBOARD_CLIENT:
      return {
        ...state,
        clientData: action.payload,
      };
    case CREATE_CASE:
      return {
        ...state,
        cases: { ...state.cases, [action.payload.id]: action.payload },
      };
    case UPDATE_CASE:
      return {
        ...state,
        cases: {
          ...state.cases,
          [action.payload.caseId]: {
            ...state.cases[action.payload.caseId],
            [action.payload.field_name]: action.payload.field_value,
          },
        },
      };
    case FETCH_CASE:
      return {
        ...state,
        selectedCase: action.payload,
      };
    case FETCH_CLIENT:
      return {
        ...state,
        selectedClient: action.payload,
      };
    case FETCH_ALL_EXTERNAL_CASES:
      return {
        ...state,
        allExternalCases: action.payload || [],
      };
    case DELETE_CASE_FIELD:
      const { [action.payload.fieldName]: _, ...remainingFields } = state.cases[action.payload.caseId]?.fields || {};
      return {
        ...state,
        cases: {
          ...state.cases,
          [action.payload.caseId]: {
            ...state.cases[action.payload.caseId],
            fields: remainingFields,
          },
        },
      };
    case ANALYZE_CASE:
      return {
        ...state,
        cases: {
          ...state.cases,
          [action.payload.caseId]: {
            ...state.cases[action.payload.caseId],
            analysis: action.payload.analysis,
          },
        },
      };
    case GENERATE_DOCUMENT_REQUEST:
      return {
        ...state,
        generatingDocument: true,
        documentGenerationError: null,
      };
    case GENERATE_DOCUMENT_SUCCESS:
      return {
        ...state,
        generatingDocument: false,
        documentGenerationError: null,
      };
    case GENERATE_DOCUMENT_FAILURE:
      return {
        ...state,
        generatingDocument: false,
        documentGenerationError: action.payload,
      };
    case 'DELETE_CLIENT_SUCCESS':
      return {
        ...state,
        allClients: state.allClients.filter(client => client.ClientID !== action.payload),
      };
    case 'DELETE_CASE_SUCCESS':
      const { case_id, client_id } = action.payload;
      if (client_id && state.cases[client_id]) {
        return {
          ...state,
          cases: {
            ...state.cases,
            [client_id]: state.cases[client_id].filter((c: any) => c.CaseID !== case_id),
          },
        };
      } else {
        return state;
      }
    case 'UPDATE_CASE_SUCCESS':
      return {
        ...state,
        cases: state.cases.map((caseItem: any) =>
          caseItem.case_id === action.payload.case_id
            ? { ...caseItem, ...action.payload }
            : caseItem
        ),
      };
    case 'UPDATE_CASE_STATUS_SUCCESS':
      return {
        ...state,
        cases: {
          ...state.cases,
          [action.payload.case_id]: {
            ...state.cases[action.payload.case_id],
            case_status: action.payload.case_status,
          },
        },
      };
    case FETCH_ALL_CLIENT_DETAILS_SUCCESS:
      return {
        ...state,
        clientDetails: { ...state.clientDetails, [action.payload.client.ClientID]: action.payload },
      };
    case 'START_IMPORT_PROGRESS':
      return {
        ...state,
        importProgress: {
          ...state.importProgress,
          active: true,
          jobId: action.payload.jobId,
          messages: [],
          percentage: 0,
        },
      };
    case 'END_IMPORT_PROGRESS':
      return {
        ...state,
        importProgress: {
          ...state.importProgress,
          active: false,
          messages: [],
          percentage: 100,
        },
      };
    case FETCH_ALL_CLIENT_DETAILS_FAILURE:
      return { ...state };
    case 'APPEND_CLIENTS':
      const newClients = Array.isArray(action.payload) ? action.payload : [];
      const existingIds = new Set(state.allClients.map((c: any) => c.ClientID));
      const uniqueNewClients = newClients.filter((c: any) => !existingIds.has(c.ClientID));
      return {
        ...state,
        allClients: [...state.allClients, ...uniqueNewClients],
      };
    case 'CLEAR_SELECTED_CLIENTS':
      return { ...state, selectedClients: [] };

    case ONBOARD_CLIENT_RESET:
      return { ...state, isConnecting: false, connected: false, steps: [], lastStep: null, ok: null, complete: false, summary: null };
    case ONBOARD_CLIENT_CONNECTING:
      return { ...state, isConnecting: true, connected: false, complete: false};

    case ONBOARD_CLIENT_CONNECTED:
      return { ...state, isConnecting: false, connected: true };
    case ONBOARD_CLIENT_STEPS: {
      const steps = (action.payload?.steps || []).map((s: any) => ({
        id: s.id,
        label: s.label,
        status: FROM_RAW[s.status] || "Pending",
        messages: [],
      })) as StepUI[];
      return { ...state, steps };
    }

    case ONBOARD_CLIENT_STEP: {
      const { id, status, message, ...rest } = action.payload || {};
      const ui = FROM_RAW[status] || "Pending";

      const nextSteps = upsertStep(state.steps, {
        id,
        status: ui,
        messages: message
          ? (() => {
            const existing = state.steps.find((s) => s.id === id)?.messages || [];
            return [...existing, String(message)];
          })()
          : state.steps.find((s) => s.id === id)?.messages || [],
        ...rest,
      });

      const lastStep = nextSteps.find((s) => s.id === id) || null;
      return { ...state, steps: nextSteps, lastStep };
    }

    case ONBOARD_CLIENT_COMPLETED: {
      const sum = action.payload || {};
      return {
        ...state,
        complete: true,
        ok: !!sum.ok,
        summary: sum,
        error: sum.ok ? null : (sum.message || "No files were accepted."),
      };
    }

    case ONBOARD_CLIENT_FAILED: {
      const msg = action.payload?.error || action.payload?.message || "File import failed.";
      return {
        ...state,
        complete: true,
        ok: false,
        error: msg,
        summary: msg,
      };
    }
    default:
      return state;
  }
};
